/**
 * A SERVER REFUSAL OF A FIELD GOES UNDER THAT FIELD.
 *
 * A 422 names the fields it refused — `{ errors: { name: ["…taken"], "meta.project_url": ["…"] } }`.
 * Shown as a toast, the reader has to find the field the message is about; shown
 * under the field, the form already points at it. A form opts in by sending its
 * request with `fieldErrors: true` (the interceptor then leaves a 422 that names
 * fields to the form) and reading the refusal through `reportFormError` (lib/api-toast).
 *
 * `map` names, for each backend key the form knows, the form's own field. A
 * nested key (`attributes.0.value`, `meta.project_url`) matches its prefix.
 */
export type FieldErrorRead<K extends string> = { fields: Partial<Record<K, string>>; rest: string[] };

export function readFieldErrors<K extends string>(error: unknown, map: Record<string, K>): FieldErrorRead<K> | null {
  const response = (error as { response?: { status?: number; data?: { errors?: unknown } } } | null)?.response;
  const errors = response?.status === 422 ? response.data?.errors : null;
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) return null;
  const fields: Partial<Record<K, string>> = {};
  const rest: string[] = [];
  for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
    const message = Array.isArray(value) ? value.find((v) => typeof v === "string") : typeof value === "string" ? value : null;
    if (!message) continue;
    const target = map[key] ?? Object.entries(map).find(([prefix]) => key.startsWith(`${prefix}.`))?.[1];
    if (target && !fields[target]) fields[target] = message;
    else if (!target) rest.push(message);
  }
  return { fields, rest };
}

/** Does this 422 name any field at all? The interceptor's question before it stays quiet. */
export function namesFields(data: unknown): boolean {
  const errors = (data as { errors?: unknown } | null)?.errors;
  return Boolean(errors && typeof errors === "object" && !Array.isArray(errors) && Object.keys(errors).length > 0);
}

